import type { PaymentMethodType } from '@chargebee/chargebee-js-types/cb-types/internal/payment-intent/types'
import { isAbortError } from '@/utils/error'

export default function usePaymentIntent(selection: Ref<CheckoutSelection>, seats: Ref<number>, coupon: Ref<string>) {
  const paymentStore = usePaymentStore()
  const businessStore = useBusinessStore()

  const isFreeUser = computed(() => !businessStore.id)

  const paymentData = reactive({
    isValid: false,
    paymentIntent: null as SubscriptionPaymentIntent | null,
    paymentPreview: null as PaymentPreview | null,
    paymentMethodType: 'card' as PaymentMethodType.DIRECT_DEBIT | PaymentMethodType.CARD,
  })

  const error = ref<Error | null>(null)

  let abortController: AbortController | null = null

  const buildPaymentIntentPayload = (billingAddress?: Address): PaymentIntentPayload | DirectPaymentIntentPayload => {
    return {
      currency: selection.value.currency,
      cycle: selection.value.billingCycle,
      plan: selection.value.plan!.id,
      seats: seats.value,
      paymentMethodType: paymentData.paymentMethodType,
      coupon: coupon.value,
      ...(isFreeUser.value && { billing: billingAddress }),
    }
  }

  const generatePaymentIntent = async (billingAddress?: Address) => {
    abortController?.abort()
    abortController = new AbortController()

    error.value = null
    try {
      const payload = buildPaymentIntentPayload(billingAddress)
      const response = await paymentStore.createPaymentIntent(payload, abortController.signal)
      paymentData.isValid = false
      const { paymentIntent: _, ...paymentPreview } = response
      paymentData.paymentIntent = response
      paymentData.paymentPreview = paymentPreview
    } catch (e) {
      if (!isAbortError(e)) {
        error.value = e instanceof Error ? e : new Error(String(e))
        paymentData.paymentIntent = null
      }
    }
  }

  const abortPaymentIntent = () => {
    abortController?.abort()
  }

  return {
    paymentData,
    generatePaymentIntent,
    abortPaymentIntent,
    error,
  }
}
